import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import MenuTreePanel from "./MenuTreePanel";
import ReplicateMenuDialog from "./ReplicateMenuDialog";
import FoodTypeDialog from "./FoodTypeDialog";
import CategoryDialog from "./CategoryDialog";
import ItemDialog from "./ItemDialog";
import VariantGroupDialog from "./VariantGroupDialog";
import VariantDialog from "./VariantDialog";
import { menuApi } from "../utils/menuApi";

const MenuManagementSection = ({
  token,
  clientId,
  selectedLocation,
  locations,
  selectedLocationId,
  onSelectLocation,
  showSnackbar,
}) => {
  const [treeLoading, setTreeLoading] = useState(false);
  const [menuTree, setMenuTree] = useState(null);

  const [selectedFoodType, setSelectedFoodType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariantGroup, setSelectedVariantGroup] = useState(null);

  const [replicateOpen, setReplicateOpen] = useState(false);
  const [foodTypeDialog, setFoodTypeDialog] = useState({ open: false, mode: "create", data: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: "create", data: null });
  const [itemDialog, setItemDialog] = useState({ open: false, mode: "create", data: null });
  const [variantGroupDialog, setVariantGroupDialog] = useState({ open: false, mode: "create", data: null });
  const [variantDialog, setVariantDialog] = useState({
    open: false,
    mode: "create",
    data: null,
    variantType: "direct",
  });

  const canManageNested = useMemo(() => !!selectedLocationId, [selectedLocationId]);

  const refreshTree = async () => {
    if (!selectedLocationId) {
      setMenuTree(null);
      return;
    }

    setTreeLoading(true);
    try {
      const response = await menuApi.getTree({ token, clientId, locationId: selectedLocationId });
      setMenuTree(response?.data || response || null);
    } catch (error) {
      setMenuTree(null);
      showSnackbar(error?.response?.data?.detail || "Failed to load menu tree", "error");
    } finally {
      setTreeLoading(false);
    }
  };

  useEffect(() => {
    refreshTree();
  }, [selectedLocationId]);

  const handleDeleteFoodType = async (foodType) => {
    if (!window.confirm(`Delete food type "${foodType.name}"?`)) return;
    try {
      await menuApi.deleteFoodType({
        token,
        clientId,
        locationId: selectedLocationId,
        foodTypeId: foodType.id,
      });
      showSnackbar("Food type deleted successfully");
      refreshTree();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to delete food type", "error");
    }
  };

  const handleDeleteCategory = async (foodTypeId, category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      await menuApi.deleteCategory({
        token,
        clientId,
        locationId: selectedLocationId,
        foodTypeId,
        categoryId: category.id,
      });
      showSnackbar("Category deleted successfully");
      refreshTree();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to delete category", "error");
    }
  };

  const handleDeleteItem = async ({ foodTypeId, categoryId, item }) => {
    if (!window.confirm(`Delete item "${item.name}"?`)) return;
    try {
      await menuApi.deleteItem({
        token,
        clientId,
        locationId: selectedLocationId,
        foodTypeId,
        categoryId,
        itemId: item.id,
      });
      showSnackbar("Item deleted successfully");
      refreshTree();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to delete item", "error");
    }
  };

  const handleDeleteVariantGroup = async ({ foodTypeId, categoryId, itemId, group }) => {
    if (!window.confirm(`Delete variant group "${group.name}"?`)) return;
    try {
      await menuApi.deleteVariantGroup({
        token,
        clientId,
        locationId: selectedLocationId,
        foodTypeId,
        categoryId,
        itemId,
        variantGroupId: group.id,
      });
      showSnackbar("Variant group deleted successfully");
      refreshTree();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to delete variant group", "error");
    }
  };

  const handleDeleteVariant = async ({
    foodTypeId,
    categoryId,
    itemId,
    variantGroupId,
    variant,
    type,
  }) => {
    if (!window.confirm(`Delete variant "${variant.name}"?`)) return;

    try {
      if (type === "group") {
        await menuApi.deleteVariantInGroup({
          token,
          clientId,
          locationId: selectedLocationId,
          foodTypeId,
          categoryId,
          itemId,
          variantGroupId,
          variantId: variant.id,
        });
      } else {
        await menuApi.deleteDirectVariant({
          token,
          clientId,
          locationId: selectedLocationId,
          foodTypeId,
          categoryId,
          itemId,
          variantId: variant.id,
        });
      }

      showSnackbar("Variant deleted successfully");
      refreshTree();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to delete variant", "error");
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Menu Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedLocation
              ? `Manage menu for ${selectedLocation.name}`
              : "Select a location to manage menu"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Selected Location"
            value={selectedLocationId}
            onChange={(e) => onSelectLocation(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canManageNested}
            onClick={() => setFoodTypeDialog({ open: true, mode: "create", data: null })}
          >
            Add Food Type
          </Button>

          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={!canManageNested}
            onClick={() => setReplicateOpen(true)}
          >
            Replicate Menu
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MenuTreePanel
            loading={treeLoading}
            menuTree={menuTree}
            onRefresh={refreshTree}
            onAddCategory={(foodType) => {
              setSelectedFoodType(foodType);
              setCategoryDialog({ open: true, mode: "create", data: null });
            }}
            onEditFoodType={(foodType) => {
              setSelectedFoodType(foodType);
              setFoodTypeDialog({ open: true, mode: "edit", data: foodType });
            }}
            onDeleteFoodType={handleDeleteFoodType}
            onAddItem={(foodType, category) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setItemDialog({ open: true, mode: "create", data: null });
            }}
            onEditCategory={(foodType, category) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setCategoryDialog({ open: true, mode: "edit", data: category });
            }}
            onDeleteCategory={handleDeleteCategory}
            onEditItem={(foodType, category, item) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setItemDialog({ open: true, mode: "edit", data: item });
            }}
            onDeleteItem={handleDeleteItem}
            onAddVariantGroup={(foodType, category, item) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setVariantGroupDialog({ open: true, mode: "create", data: null });
            }}
            onEditVariantGroup={(foodType, category, item, group) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setSelectedVariantGroup(group);
              setVariantGroupDialog({ open: true, mode: "edit", data: group });
            }}
            onDeleteVariantGroup={handleDeleteVariantGroup}
            onAddDirectVariant={(foodType, category, item) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setVariantDialog({
                open: true,
                mode: "create",
                data: null,
                variantType: "direct",
              });
            }}
            onAddGroupedVariant={(foodType, category, item, group) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setSelectedVariantGroup(group);
              setVariantDialog({
                open: true,
                mode: "create",
                data: null,
                variantType: "group",
              });
            }}
            onEditVariant={(foodType, category, item, group, variant, type) => {
              setSelectedFoodType(foodType);
              setSelectedCategory(category);
              setSelectedItem(item);
              setSelectedVariantGroup(group || null);
              setVariantDialog({
                open: true,
                mode: "edit",
                data: variant,
                variantType: type,
              });
            }}
            onDeleteVariant={handleDeleteVariant}
          />
        </Grid>
      </Grid>

      <ReplicateMenuDialog
        open={replicateOpen}
        onClose={() => setReplicateOpen(false)}
        locations={locations}
        targetLocationId={selectedLocationId}
        token={token}
        clientId={clientId}
        menuTree={menuTree}
        onSuccess={() => {
          setReplicateOpen(false);
          refreshTree();
        }}
        showSnackbar={showSnackbar}
      />

      <FoodTypeDialog
        open={foodTypeDialog.open}
        mode={foodTypeDialog.mode}
        data={foodTypeDialog.data}
        token={token}
        clientId={clientId}
        locationId={selectedLocationId}
        onClose={() => setFoodTypeDialog({ open: false, mode: "create", data: null })}
        onSuccess={refreshTree}
        showSnackbar={showSnackbar}
      />

      <CategoryDialog
        open={categoryDialog.open}
        mode={categoryDialog.mode}
        data={categoryDialog.data}
        token={token}
        clientId={clientId}
        locationId={selectedLocationId}
        foodType={selectedFoodType}
        onClose={() => setCategoryDialog({ open: false, mode: "create", data: null })}
        onSuccess={refreshTree}
        showSnackbar={showSnackbar}
      />

      <ItemDialog
        open={itemDialog.open}
        mode={itemDialog.mode}
        data={itemDialog.data}
        token={token}
        clientId={clientId}
        locationId={selectedLocationId}
        foodType={selectedFoodType}
        category={selectedCategory}
        onClose={() => setItemDialog({ open: false, mode: "create", data: null })}
        onSuccess={refreshTree}
        showSnackbar={showSnackbar}
      />

      <VariantGroupDialog
        open={variantGroupDialog.open}
        mode={variantGroupDialog.mode}
        data={variantGroupDialog.data}
        token={token}
        clientId={clientId}
        locationId={selectedLocationId}
        foodType={selectedFoodType}
        category={selectedCategory}
        item={selectedItem}
        onClose={() => setVariantGroupDialog({ open: false, mode: "create", data: null })}
        onSuccess={refreshTree}
        showSnackbar={showSnackbar}
      />

      <VariantDialog
        open={variantDialog.open}
        mode={variantDialog.mode}
        data={variantDialog.data}
        variantType={variantDialog.variantType}
        token={token}
        clientId={clientId}
        locationId={selectedLocationId}
        foodType={selectedFoodType}
        category={selectedCategory}
        item={selectedItem}
        variantGroup={selectedVariantGroup}
        onClose={() =>
          setVariantDialog({ open: false, mode: "create", data: null, variantType: "direct" })
        }
        onSuccess={refreshTree}
        showSnackbar={showSnackbar}
      />
    </Box>
  );
};

export default MenuManagementSection;